import React from "react";
import { Calendar, Drawer, Tag, Empty, Spin } from "antd";
import {
  UserOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  HomeOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { formatFechaConDia } from "../../utils/formatDate";
import {
  useAseosCalendarLogic,
  AseoResumen,
} from "./useAseosCalendarLogic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import EstadiaCodeTag from "../../Components/EstadiaCodeTag";
import AseoCodeTag from "../../Components/AseoCodeTag";

const estadoColor: Record<string, string> = {
  pendiente: "orange",
  en_proceso: "blue",
  completado: "green",
  pagado: "purple",
};

const estadoLabel: Record<string, string> = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  completado: "Completado",
  pagado: "Pagado",
};

const estadoBg: Record<string, string> = {
  pendiente: "#fff7ed",
  en_proceso: "#eff6ff",
  completado: "#f0fdf4",
  pagado: "#faf5ff",
};

const estadoBorder: Record<string, string> = {
  pendiente: "#fed7aa",
  en_proceso: "#bfdbfe",
  completado: "#bbf7d0",
  pagado: "#e9d5ff",
};

const AseosCalendar: React.FC = () => {
  const {
    currentMonth,
    handleMonthChange,
    loading,
    selectedDate,
    setSelectedDate,
    aseosForDate,
    getCountForDate,
  } = useAseosCalendarLogic();

  const dateCellRender = (date: Dayjs) => {
    const key = date.format("YYYY-MM-DD");
    const count = getCountForDate(key);
    if (count === 0) return null;

    const intensity = Math.min(count, 5);
    const opacity = 0.15 + intensity * 0.15;

    return (
      <div
        className="aseos-cal-badge"
        style={{
          // Mismo matiz que los aseos en /calendario (sky #0EA5E9 = rgb(14,165,233))
          // — antes usaba rgb(71,190,245), divergente con el color del calendario.
          background: `rgba(14, 165, 233, ${opacity})`,
          color: intensity >= 3 ? "#075985" : "#0EA5E9",
          borderRadius: 8,
          padding: "2px 8px",
          textAlign: "center",
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        {count} aseo{count !== 1 ? "s" : ""}
      </div>
    );
  };

  const onSelectDate = (date: Dayjs, info: { source: string }) => {
    // AntD dispara onSelect también al navegar mes/año (info.source
    // "month"/"year"), arrastrando el día del mes actual (p.ej. hoy = 6). Eso
    // abría el drawer en un día que casi nunca tiene aseos ("No hay aseos en
    // esta fecha") aunque el mes sí los tenga. Solo abrimos el detalle cuando el
    // usuario hace click en una FECHA concreta de la grilla.
    if (info.source !== "date") return;
    const key = date.format("YYYY-MM-DD");
    setSelectedDate(key);
  };

  return (
    <div className="aseos-calendar-container">
      <Spin spinning={loading}>
        <Calendar
          value={currentMonth}
          onPanelChange={handleMonthChange}
          onSelect={onSelectDate}
          cellRender={(current, info) => {
            if (info.type === "date") return dateCellRender(current as Dayjs);
            return null;
          }}
        />
      </Spin>

      <Drawer
        title={
          selectedDate
            ? `Aseos del ${formatFechaConDia(selectedDate, { mayuscula: false })}`
            : "Detalle de aseos"
        }
        open={!!selectedDate}
        onClose={() => setSelectedDate(null)}
        width={480}
      >
        {aseosForDate.length === 0 ? (
          <Empty description="No hay aseos en esta fecha" />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <Tag style={{ fontSize: 13, padding: "2px 10px" }}>
                {aseosForDate.length} aseo{aseosForDate.length !== 1 ? "s" : ""}
              </Tag>
              {Object.entries(
                aseosForDate.reduce<Record<string, number>>((acc, a) => {
                  acc[a.estado] = (acc[a.estado] ?? 0) + 1;
                  return acc;
                }, {}),
              ).map(([estado, count]) => (
                <Tag
                  key={estado}
                  color={estadoColor[estado] ?? "default"}
                  style={{ fontSize: 13, padding: "2px 10px" }}
                >
                  {count} {estadoLabel[estado] ?? estado}
                </Tag>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {aseosForDate.map((aseo) => (
                <AseoCard key={aseo.id} aseo={aseo} />
              ))}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

const AseoCard: React.FC<{ aseo: AseoResumen }> = ({ aseo }) => {
  const bg = estadoBg[aseo.estado] ?? "#fafbfc";
  const border = estadoBorder[aseo.estado] ?? "#edf2f7";

  return (
    <div
      style={{
        background: bg,
        borderRadius: 10,
        border: `1px solid ${border}`,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 14px",
          borderBottom: `1px solid ${border}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <HomeOutlined style={{ color: "#64748b" }} />
          <strong style={{ fontSize: 14 }}>
            {formatPropertyName(aseo.propiedad?.nombre) || "Sin propiedad"}
          </strong>
          <AseoCodeTag id={aseo.id} withTooltip={false} />
          {aseo.reservaId != null && (
            <EstadiaCodeTag id={aseo.reservaId} withTooltip={false} />
          )}
        </div>
        <Tag
          color={estadoColor[aseo.estado] ?? "default"}
          style={{ margin: 0 }}
        >
          {estadoLabel[aseo.estado] ?? aseo.estado}
        </Tag>
      </div>

      {/* Body — 2 columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px 16px",
          padding: "10px 14px",
          fontSize: 13,
          color: "#4a5568",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <UserOutlined style={{ color: "#94a3b8", fontSize: 12 }} />
          <span style={{ color: "#94a3b8" }}>Aseador</span>
        </div>
        <div style={{ fontWeight: 500 }}>
          {aseo.aseador?.nombre ?? "Sin asignar"}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ClockCircleOutlined style={{ color: "#94a3b8", fontSize: 12 }} />
          <span style={{ color: "#94a3b8" }}>Hora</span>
        </div>
        <div style={{ fontWeight: 500 }}>
          {aseo.horaAseo
            ? (dayjs(aseo.horaAseo).isValid()
                ? dayjs(aseo.horaAseo).format("HH:mm")
                : String(aseo.horaAseo).slice(0, 5))
            : aseo.urgencia ?? "—"}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <DollarOutlined style={{ color: "#94a3b8", fontSize: 12 }} />
          <span style={{ color: "#94a3b8" }}>Pago aseador</span>
        </div>
        <div style={{ fontWeight: 500 }}>
          ${Number(aseo.sueldoAseador ?? 0).toLocaleString("es-CL")}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <DollarOutlined style={{ color: "#94a3b8", fontSize: 12 }} />
          <span style={{ color: "#94a3b8" }}>Costo limpieza</span>
        </div>
        <div style={{ fontWeight: 500 }}>
          ${Number(aseo.costoLimpieza ?? 0).toLocaleString("es-CL")}
        </div>
      </div>

      {/* Observaciones — only if present */}
      {aseo.observaciones ? (
        <div
          style={{
            padding: "8px 14px 10px",
            borderTop: `1px solid ${border}`,
            fontSize: 12,
            color: "#64748b",
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
          }}
        >
          <FileTextOutlined style={{ marginTop: 2 }} />
          <span>{aseo.observaciones}</span>
        </div>
      ) : null}
    </div>
  );
};

export default AseosCalendar;
